import React, { Suspense } from 'react';
import { Redirect, BrowserRouter as Router, Switch } from "react-router-dom";
import PublicRoute from "./PublicRoute";

import PrivateRoute from "./PrivateRoute";
import AuthRoute from "./AuthRoute";
import ClientChooserRoute from "./ClientChooserRoute";
import Loader from "../components/organisms/Loader";
import ProtectedRoute from "./ProtectedRoute";
import { verifyFeedbackAccess } from "../services/feedback";
import { verifyGuideAccess } from '../services/InterviewGuide';
import RouteTracker from "./RouteTracker";
import Retriggered from '../components/pages/Retriggered/Retriggered';
import InterviewCreated from "../components/pages/InterviewCreated/index"
import TokenError from '../components/pages/Retriggered/TokenError';
import { useSelector } from 'react-redux';
import PrescreeningPreview from '../components/pages/Prescreening/PrescreeningPreview';

const AuthorizedLogin = React.lazy(() => import('../services/AuthorizedLogin'));
const ClientChooser = React.lazy(() => import('../components/pages/ClientChooser'));
const Dashboard = React.lazy(() => import('../components/pages/Dashboard/Dashboard'));
const LoginPage = React.lazy(() => import('../components/pages/LoginPage'));
const PositionForm = React.lazy(() => import('../components/pages/PositionForm'));
const PositionList = React.lazy(() => import('../components/pages/PositionList/PositionList'));
const InterviewList = React.lazy(() => import('../components/pages/InterviewList'));
const InterviewForm = React.lazy(() => import('../components/pages/InterviewForm'));
const ProfileNew = React.lazy(() => import('../components/pages/Profile/ProfileNew'));
const Account = React.lazy(() => import('../components/pages/Handle'));
const History = React.lazy(() => import('../components/pages/History'));
const Search = React.lazy(() => import('../components/pages/SearchCandidate'));
const InterviewSlots = React.lazy(() => import('../components/pages/InterviewSlots'));
const ThankYouPage = React.lazy(() => import('../components/pages/ThankYouPage'));
const Setting = React.lazy(() => import('../components/pages/SettingPage'));
const Settings = React.lazy(() => import('../components/pages/Settings'));
const Pipeline = React.lazy(() => import('../components/pages/Pipeline'));
const ResetPassword = React.lazy(() => import('../components/pages/ResetPassword'));
const ForgotPassword = React.lazy(() => import('../components/pages/ForgotPassword'));
const AddNewInterview = React.lazy(() => import('../components/pages/AddNewInterview'));
const AddNewPosition = React.lazy(() => import('../components/pages/AddNewPosition'));
const PositionBackground = React.lazy(() => import('../components/pages/AddNewPosition/PositionBackground'));
const PositionGuideLinks = React.lazy(() => import('../components/pages/AddNewPosition/PositionGuideLinks'));
const Feedback = React.lazy(() => import('../components/pages/Feedback/index'));
const Integration = React.lazy(() => import('../components/pages/IntegrationPage'));
const IntegrationJobs = React.lazy(() => import('../components/pages/IntegrationJobs'));
const Cancel = React.lazy(() => import('../components/pages/CancelInterview'));
const InterviewGuides = React.lazy(() => import("../components/pages/InterviewGuides/GuideForm"));
const GuideList = React.lazy(() => import('../components/pages/InterviewGuides/GuideList'));
const GuidePreview = React.lazy(() => import('../components/pages/InterviewGuides/GuidePreview'));
const RetryPageIntegration = React.lazy(() => import('../components/pages/RetryPageIntegration/RetryPageIntegration'));
const SlackIntegration = React.lazy(() => import('../components/pages/SlackIntegration'));
const CompanyProfile = React.lazy(() => import('../components/pages/Settings/CompanyProfile'));
const PrescreeningLogin =React.lazy(() => import('../components/pages/Prescreening/PrescreeningLogin'));
const PrescreeningTest =React.lazy(() => import('../components/pages/Prescreening/PrescreeningTest'));
const PrescreeningReport =React.lazy(() => import('../components/pages/Prescreening/PrescreeningReport'));
const PrescreeningList =React.lazy(() => import('../components/pages/Prescreening/PrescreeningList'));
const PrescreeningForm =React.lazy(() => import('../components/pages/Prescreening/PrescreeningForm'));

const AppRouter = ({ isAuthenticated, isClientSelected }) => {
    const state = useSelector(state => state);
    return (
        <Suspense fallback={<Loader/>}>
            <Router>
                <Switch>
                    <PublicRoute exact path="/authorize" component={AuthorizedLogin} />
                    <AuthRoute exact path="/login" component={LoginPage} isAuthenticated={isAuthenticated} />
                    <ClientChooserRoute
                        exact path="/client-chooser"
                        component={ClientChooser}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/dashboard"
                        component={Dashboard}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/positions/background"
                        component={PositionBackground}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                   <PrivateRoute
                        exact path="/positions/guide-links"
                        component={PositionGuideLinks}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/interview-guides"
                        component={GuideList}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                     <PrivateRoute
                        exact path="/interview-guides/add"
                        component={InterviewGuides}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/positions/new/:activePage?"
                        component={AddNewPosition}
                        // component={PositionForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        path="/positions/:id"
                        component={AddNewPosition}
                        // component={PositionForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        path="/interview/new/:activePage?"
                        component={AddNewInterview}
                        // component={InterviewForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="NEW"
                    />

                    <PrivateRoute
                        path="/prescreenTest/edit"
                        component={AddNewInterview}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="PRESCREEN-EDIT"
                    />

                    <PrivateRoute
                        path="/prescreenTest/New"
                        component={AddNewInterview}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="PRESCREEN-NEW"
                    />  

                      <PrivateRoute
                        path="/prescreenTest/schedule"
                        component={AddNewInterview}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="PRESCREEN-SCHEDULE"
                    />

                    <PrivateRoute
                        exact path="/interview/edit/:activePage?"
                        component={AddNewInterview}
                        // component={InterviewForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="EDIT"
                    />
                    <PrivateRoute
                        exact path="/interview/reschedule"
                        component={AddNewInterview}
                        // component={InterviewForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="RESCHEDULE"
                    />
                    <PrivateRoute
                        exact path="/interview/schedule"
                        component={AddNewInterview}
                        // component={InterviewForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="SCHEDULE"
                    />
                    <PrivateRoute
                        exact path="/interview/scheduleNext"
                        component={AddNewInterview}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                        action="scheduleNext"
                    />
                    <PrivateRoute
                        path="/interviews/:tab?"
                        component={InterviewList}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/positions"
                        component={PositionList}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/profile"
                        component={ProfileNew}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/account"
                        component={Account}
                        isAuthenticated={isAuthenticated}
                    />
                    <PrivateRoute
                        exact path="/position/edit/:id/:activePage?/:status?"
                        component={AddNewPosition}
                        // component={PositionForm}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/candidate/search"
                        component={Search}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/history"
                        component={History}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/slack-integration"
                        component={SlackIntegration}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                     <PrivateRoute
                        exact path="/company-profile"
                        component={CompanyProfile}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    {/* <PrivateRoute
                        path='/setting/:tab?'
                        component={Setting}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    /> */}
                    <PrivateRoute
                        path='/settings/:tab?'
                        component={Settings}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PrivateRoute
                        exact path="/pipeline/:position?"
                        component={Pipeline}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                     <PrivateRoute
                        exact path="/integration/:client?/:status?/:subStatus?"
                        component={Integration}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />

                     <PrivateRoute
                        exact path="/integration-jobs"
                        component={IntegrationJobs}
                        isAuthenticated={isAuthenticated}
                        isClientSelected={isClientSelected}
                    />
                    <PublicRoute path="/reset" component={ResetPassword} />
                    <PublicRoute path="/forgot-password" component={ForgotPassword} />
                    <PublicRoute path='/interviewSlots' component={InterviewSlots} action="SLOTS"/>
                    <PublicRoute path='/thankYou' component={ThankYouPage} />
                    <PublicRoute path='/interview-feedback' component={Feedback} />
                    <PublicRoute path='/cancel-interview' component={Cancel} />
                    <PublicRoute path='/reschedule-interview' component={InterviewSlots} action="RESCHEDULE" />
                    <PublicRoute path="/retry-lever-opportunity/:token?" component={Retriggered} />
                    <PublicRoute path="/retry-stage-change/:token?/:integration?" component={RetryPageIntegration} />
                    <PublicRoute path="/next-round-interview/:token?" component={InterviewCreated} />
                    <PublicRoute path="/create-round-interview/:token?" component={TokenError} />
                    <PublicRoute path="/interview-guides/preview" component={GuidePreview} />
                    <PublicRoute path="/candidate_pre_screening_round/:token?" component={PrescreeningLogin} />
                    <PublicRoute path="/prescreening/test" component={PrescreeningTest}/>
                    <PublicRoute path="/prescreening/list" component={PrescreeningList}/>
                    <PublicRoute path="/prescreening/add" component={PrescreeningForm}/>
                    <PublicRoute path="/prescreening/preview" component={PrescreeningPreview}/>
                    <PublicRoute path={`/prescreen/report/:client_id?/:position_id?/:candidate_id?/:prescreen_id?/:prescreen_round_id?`} component={PrescreeningReport} />

                    <ProtectedRoute
                        path="/feedback"
                        redirect="/interview-feedback?i_id=${}"
                        params={["interview_id"]}
                        authenticator={verifyFeedbackAccess}
                        isAuthenticated={isAuthenticated}
                    />
                    <ProtectedRoute
                        path="/guides"
                        redirect="/interview-guides/preview?guide_id=${}&position_id=${}"
                        params={["guide_id","position_id"]}
                        authenticator={verifyGuideAccess}
                        isAuthenticated={isAuthenticated}
                    />
                    <Redirect from="*" to="/dashboard" />
                </Switch>
                <RouteTracker/>
            </Router>
        </Suspense>
    );
};

export default AppRouter;
